import { IconProp, Tag, TagProps } from '@ff-it/ui';
import { ForwardedRef, forwardRef, ReactElement, ReactNode } from 'react';

export type StateVariantMap<State extends string> = Record<
  State,
  {
    icon: IconProp;
    variant: TagProps['variant'];
  }
>;

export type CreateStateTagOptions<State extends string> = {
  testId: string;
  stateVariantMap: StateVariantMap<State>;
  displayTransform?: (state: State) => string;
};

export type StateTagProps<State extends string> = {
  state: State;
  children?: ReactNode;
  iconOnly?: boolean;
} & Omit<TagProps, 'icon' | 'children'>;

export function createStateTag<State extends string>({
  testId,
  stateVariantMap,
  displayTransform,
}: CreateStateTagOptions<State>) {
  const StateTag = forwardRef(function StateTag(
    { state, children, iconOnly, ...tagProps }: StateTagProps<State>,
    ref: ForwardedRef<HTMLDivElement>,
  ): ReactElement {
    const displayText = displayTransform ? displayTransform(state) : state;

    return (
      <Tag {...stateVariantMap[state]} ref={ref} testId={testId} data-state={state} {...tagProps}>
        {!iconOnly && displayText}
        {children}
      </Tag>
    );
  });

  return StateTag;
}
